import {
  filterMemberDeposits,
  filterMemberWithdrawals,
  memberPaymentsDateRangeRequest
} from '@bo-utils/members/memberPayments';
import { useCallback, useMemo, useState } from 'react';
import { DEFAULT_MEMBER_PAYMENTS } from '@bo-constants/member/memberPayments';
import type { MemberPaymentsSearchFilters } from '@library/types/memberPayments';
import type { PaymentsRequest } from '@library/types/payments';
import { useGetMemberPaymentsTransactionsQuery } from '@bo-services/members/memberPaymentsApi';
import { useParams } from 'react-router-dom';

export interface MemberPaymentsProps {
  paymentsSearchFilters: MemberPaymentsSearchFilters;
}

export const useMemberPayments = ({
  paymentsSearchFilters
}: MemberPaymentsProps) => {
  const { memberId } = useParams<{ memberId?: string }>();
  const [currentPage, setCurrentPage] = useState<number>(1);

  const memberPaymentsRequest: PaymentsRequest = useMemo(() => {
    const { dateRange } = paymentsSearchFilters;
    const { formattedFromDate, formattedToDate } =
      memberPaymentsDateRangeRequest(dateRange);

    const request = {
      ...(memberId && { id: memberId }),
      ...(formattedFromDate && { fromDate: formattedFromDate }),
      ...(formattedToDate && { toDate: formattedToDate }),
      page: currentPage
    };

    return request;
  }, [currentPage, memberId, paymentsSearchFilters]);

  const { data, isLoading, isFetching, isError } =
    useGetMemberPaymentsTransactionsQuery(
      {
        ...memberPaymentsRequest
      },
      {
        skip: !memberId
      }
    );

  const { deposits, withdrawals, user } = useMemo(() => {
    if (data) {
      return data;
    }
    return DEFAULT_MEMBER_PAYMENTS;
  }, [data]);

  const filteredDeposits = useMemo(
    () => filterMemberDeposits(deposits, paymentsSearchFilters),
    [deposits, paymentsSearchFilters]
  );

  const filteredWithdrawals = useMemo(
    () => filterMemberWithdrawals(withdrawals, paymentsSearchFilters),
    [withdrawals, paymentsSearchFilters]
  );

  const handleCurrentPageChange = useCallback(() => {
    setCurrentPage((prevPage) => prevPage + 1);
  }, []);

  return {
    deposits: filteredDeposits,
    isPaymentsError: isError,
    isPaymentsLoading: isFetching || isLoading,
    memberData: user,
    onCurrentPageChange: handleCurrentPageChange,
    paymentsData: data,
    withdrawals: filteredWithdrawals
  };
};
